import { ref, readonly } from 'vue'
import type { BibleVersion, BibleBookOption } from '../types/bible'
import { apiClient } from '../services/apiClient'

// 全局聖經數據
const versions = ref<BibleVersion[]>([])
const books = ref<BibleBookOption[]>([])
const booksCache = ref<Record<string, BibleBookOption[]>>({}) // 緩存每個版本的書籍
const loading = ref(false)
const error = ref<string | null>(null)

export function useGlobalBibleData() {
  const loadVersions = async () => {
    // 如果已經加載過，就不再加載
    if (versions.value.length > 0) {
      return
    }

    loading.value = true
    try {
      error.value = null
      const data = await apiClient.getVersions()
      versions.value = data
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Failed to load versions'
    } finally {
      loading.value = false
    }
  }

  const loadBooks = async (versionCode: string) => {
    // 檢查該版本的書籍是否已加載過
    if (booksCache.value[versionCode]) {
      books.value = booksCache.value[versionCode]
      return
    }

    loading.value = true
    try {
      error.value = null
      const data = await apiClient.getBooks(versionCode)
      const mappedBooks = data.map((b: any) => ({
        id: b.id,
        chiShort: b.chi_short,
        chiFull: b.chi_full,
        engShort: b.eng_short,
        engFull: b.eng_full,
        chapterCount: b.chapterCount || 1,
      }))
      // 存儲到緩存
      booksCache.value[versionCode] = mappedBooks
      books.value = mappedBooks
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Failed to load books'
    } finally {
      loading.value = false
    }
  }

  return {
    versions: readonly(versions),
    books: readonly(books),
    loading: readonly(loading),
    error: readonly(error),
    loadVersions,
    loadBooks,
  }
}
